/* dvrw-shop-the-look — portable Shopify section (functional style) */
;(() => {
  if (window.DvrwShopTheLook) return;

  // ---------- Pure helpers ----------

  const trackEvent = (name, payload) => {
    if (!window.dataLayer || typeof window.dataLayer.push !== 'function') return;
    window.dataLayer.push({ event: name, ...payload });
  };

  const formatMoney = (cents) => {
    if (cents == null) return '';
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      const fmt = Shopify.money_format || (Shopify.currency && Shopify.currency.format);
      if (fmt) { try { return Shopify.formatMoney(cents, fmt); } catch (_) {} }
    }
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
    const locale = (document.documentElement.lang || navigator.language || 'en').split(',')[0];
    try { return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100); }
    catch (_) { return `${(cents / 100).toFixed(2)} ${currency}`; }
  };

  const trapFocus = (container) => {
    const sel = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const nodes = container.querySelectorAll(sel);
    if (!nodes.length) return () => {};
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  };

  const dispatch = (name, detail) =>
    document.dispatchEvent(new CustomEvent(name, { detail }));

  const buildLines = (modalItemsEl) =>
    Array.from(modalItemsEl.querySelectorAll('.dvrw-stl__modal-item'))
      .map((li) => ({ li, active: li.querySelector('.dvrw-stl__variant.is-active') }))
      .filter(({ active }) => active !== null)
      .map(({ li, active }) => ({
        variantId: parseInt(active.dataset.variantId, 10),
        productId: li.dataset.productId,
        productHandle: li.dataset.productHandle
      }))
      .filter(({ variantId }) => !isNaN(variantId));

  const collectRefs = (container) => ({
    container,
    id: container.dataset.sectionId,
    slider: container.querySelector('[data-stl-slider]'),
    prevBtn: container.querySelector('[data-stl-prev]'),
    nextBtn: container.querySelector('[data-stl-next]'),
    modal: container.querySelector('[data-stl-modal]'),
    modalItemsEl: container.querySelector('[data-stl-modal] [data-stl-modal-items]'),
    modalError: container.querySelector('[data-stl-modal] [data-stl-modal-error]'),
    addAllBtn: container.querySelector('[data-stl-modal] [data-stl-modal-add-all]')
  });

  // ---------- DOM effects ----------

  const cloneSlideTemplate = (container, blockId, target) => {
    const tpl = container.querySelector(`[data-stl-slide-items="${blockId}"]`);
    if (!tpl || !tpl.content) return false;
    target.innerHTML = '';
    target.appendChild(tpl.content.cloneNode(true));
    return true;
  };

  const showModal = (modal) => {
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };

  const hideModal = (modal) => {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  const setHotspotState = (h, open) => {
    h.classList.toggle('is-open', open);
    const btn = h.querySelector('[data-stl-marker]');
    const pop = h.querySelector('[data-stl-popover]');
    if (btn) btn.setAttribute('aria-expanded', String(open));
    if (pop) pop.setAttribute('aria-hidden', String(!open));
  };

  // ---------- Slider (Bring Your Own) ----------

  const initSlider = (refs) => {
    if (!refs.slider) return () => {};
    const adapter = window.dvrwStl && window.dvrwStl.sliderAdapter;
    if (typeof adapter !== 'function') {
      return () => {};
    }
    const teardown = adapter({
      slider: refs.slider,
      prevBtn: refs.prevBtn,
      nextBtn: refs.nextBtn,
      slidesCount: refs.slider.querySelectorAll('[data-stl-slide]').length,
      container: refs.container
    });
    return typeof teardown === 'function' ? teardown : () => {};
  };

  // ---------- Hotspots ----------

  const initHotspots = ({ container }) => {
    const cleanups = [];

    const closeAll = () => {
      container.querySelectorAll('[data-stl-hotspot].is-open')
        .forEach((h) => setHotspotState(h, false));
    };

    const openOne = (h) => {
      closeAll();
      setHotspotState(h, true);
      const slide = h.closest('[data-stl-slide]');
      trackEvent('look_hotspot_open', {
        slide_index: slide ? slide.dataset.blockId : null,
        product_id: h.dataset.productId,
        hotspot_index: h.dataset.hotspotIndex
      });
    };

    const toggle = (h) => {
      if (!h) return;
      if (h.classList.contains('is-open')) setHotspotState(h, false);
      else openOne(h);
    };

    container.querySelectorAll('[data-stl-marker]').forEach((btn) => {
      const stop = (e) => e.stopPropagation();
      const onClick = (e) => { e.preventDefault(); e.stopPropagation(); toggle(btn.closest('[data-stl-hotspot]')); };
      btn.addEventListener('pointerdown', stop);
      btn.addEventListener('mousedown', stop);
      btn.addEventListener('touchstart', stop, { passive: true });
      btn.addEventListener('click', onClick);
      cleanups.push(() => {
        btn.removeEventListener('pointerdown', stop);
        btn.removeEventListener('mousedown', stop);
        btn.removeEventListener('touchstart', stop);
        btn.removeEventListener('click', onClick);
      });
    });

    container.querySelectorAll('[data-stl-hotspot]').forEach((h) => {
      const x = parseFloat(h.style.getPropertyValue('--stl-x'));
      if (!isNaN(x) && x > 65) h.setAttribute('data-flip-x', 'left');

      let timer;
      const onEnter = () => {
        if (window.matchMedia && window.matchMedia('(hover: none)').matches) return;
        clearTimeout(timer);
        openOne(h);
      };
      const onLeave = () => {
        if (window.matchMedia && window.matchMedia('(hover: none)').matches) return;
        timer = setTimeout(() => setHotspotState(h, false), 200);
      };
      h.addEventListener('mouseenter', onEnter);
      h.addEventListener('mouseleave', onLeave);
      cleanups.push(() => {
        h.removeEventListener('mouseenter', onEnter);
        h.removeEventListener('mouseleave', onLeave);
      });
    });

    return { closeAll, cleanup: () => cleanups.forEach((c) => c()) };
  };

  // ---------- Section factory ----------

  const createSection = (container) => {
    const refs = collectRefs(container);

    let state = { currentBlockId: null, releaseTrap: null };
    const setState = (patch) => { state = { ...state, ...patch }; };

    const teardownSlider = initSlider(refs);
    const hotspots = initHotspots(refs);

    const updateAddAllState = () => {
      if (!refs.addAllBtn || !refs.modalItemsEl) return;
      const any = refs.modalItemsEl.querySelector('.dvrw-stl__variant.is-active') !== null;
      refs.addAllBtn.disabled = !any;
    };

    const bindVariantClicks = () => {
      refs.modalItemsEl.querySelectorAll('.dvrw-stl__modal-item').forEach((li) => {
        const btns = li.querySelectorAll('.dvrw-stl__variant:not(.is-soldout)');
        btns.forEach((btn) => {
          btn.addEventListener('click', () => {
            btns.forEach((b) => b.classList.remove('is-active'));
            btn.classList.add('is-active');
          });
        });
      });
    };

    const openModal = (blockId, modalTitle) => {
      if (!refs.modal || !refs.modalItemsEl) return;
      if (!cloneSlideTemplate(refs.container, blockId, refs.modalItemsEl)) return;

      hideModalError();
      bindVariantClicks();
      updateAddAllState();

      if (modalTitle) {
        const titleEl = refs.modal.querySelector('.dvrw-stl__modal-title');
        if (titleEl) titleEl.textContent = modalTitle;
      }

      showModal(refs.modal);
      setState({ currentBlockId: blockId, releaseTrap: trapFocus(refs.modal) });

      const closeBtn = refs.modal.querySelector('[data-stl-modal-close]');
      if (closeBtn) closeBtn.focus();

      const ids = Array.from(refs.modalItemsEl.querySelectorAll('[data-product-id]'))
        .map((li) => li.dataset.productId);
      trackEvent('look_modal_open', { slide_index: blockId, product_ids: ids });
    };

    const closeModal = () => {
      if (!refs.modal) return;
      hideModal(refs.modal);
      if (state.releaseTrap) { state.releaseTrap(); setState({ releaseTrap: null }); }
    };

    const setAddAllLoading = (loading) => {
      refs.addAllBtn.classList.toggle('is-loading', loading);
      refs.addAllBtn.disabled = loading;
      refs.addAllBtn.setAttribute('aria-busy', String(loading));
    };

    let errorTimer = null;
    const showModalError = (text) => {
      if (!refs.modalError) return;
      refs.modalError.textContent = text;
      refs.modalError.hidden = false;
      if (errorTimer) clearTimeout(errorTimer);
      errorTimer = setTimeout(() => { refs.modalError.hidden = true; }, 5000);
    };
    const hideModalError = () => {
      if (!refs.modalError) return;
      if (errorTimer) clearTimeout(errorTimer);
      refs.modalError.hidden = true;
      refs.modalError.textContent = '';
    };

    const addAll = () => {
      const items = buildLines(refs.modalItemsEl);
      if (!items.length) return;
      hideModalError();
      const errFallback = container.dataset.errorMessage || 'Could not add to cart.';
      const sections = container.dataset.cartSections || undefined;
      const lines = items.map(({ variantId }) => ({ id: variantId, quantity: 1 }));

      if (!window.dvrwCart) {
        showModalError(errFallback);
        console.warn('[dvrw-stl] shopify-cart-kit (window.dvrwCart) is not loaded');
        return;
      }

      setAddAllLoading(true);

      window.dvrwCart.add({ items: lines, sections, source: 'dvrw-stl' })
        .then((detail) => {
          setAddAllLoading(false);
          dispatch('dvrw:stl:added', detail);
          trackEvent('look_add_to_cart_all', {
            slide_index: state.currentBlockId,
            product_ids: items.map((it) => it.productId),
            variant_ids: items.map((it) => it.variantId)
          });
          closeModal();
        })
        .catch((err) => {
          setAddAllLoading(false);
          const msg = (err && err.message) || errFallback;
          showModalError(msg);
          console.warn('[dvrw-stl] add-all failed', err);
        });
    };

    // CTA buttons -> openModal
    const ctaCleanups = [];
    container.querySelectorAll('[data-stl-modal-open]').forEach((btn) => {
      const stop = (e) => e.stopPropagation();
      const onClick = (e) => { e.preventDefault(); e.stopPropagation(); openModal(btn.dataset.blockId, btn.dataset.modalTitle); };
      btn.addEventListener('pointerdown', stop);
      btn.addEventListener('mousedown', stop);
      btn.addEventListener('click', onClick);
      ctaCleanups.push(() => {
        btn.removeEventListener('pointerdown', stop);
        btn.removeEventListener('mousedown', stop);
        btn.removeEventListener('click', onClick);
      });
    });

    // Modal close + add-all buttons
    const modalCleanups = [];
    if (refs.modal) {
      refs.modal.querySelectorAll('[data-stl-modal-close]').forEach((el) => {
        const h = () => closeModal();
        el.addEventListener('click', h);
        modalCleanups.push(() => el.removeEventListener('click', h));
      });
      if (refs.addAllBtn) {
        const h = () => addAll();
        refs.addAllBtn.addEventListener('click', h);
        modalCleanups.push(() => refs.addAllBtn.removeEventListener('click', h));
      }
    }

    // Document-level
    const onDocClick = (e) => {
      if (!e.target.closest('[data-stl-hotspot]')) hotspots.closeAll();
    };
    const onDocKey = (e) => {
      if (e.key !== 'Escape') return;
      if (refs.modal && refs.modal.classList.contains('is-open')) closeModal();
      else hotspots.closeAll();
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onDocKey);

    const destroy = () => {
      teardownSlider();
      hotspots.cleanup();
      ctaCleanups.forEach((c) => c());
      modalCleanups.forEach((c) => c());
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onDocKey);
      if (state.releaseTrap) state.releaseTrap();
    };

    return { destroy, getState: () => state };
  };

  // ---------- Boot ----------

  const instances = new Map();

  const initSection = (container) => {
    if (!container) return;
    const id = container.dataset.sectionId;
    if (instances.has(id)) return;
    instances.set(id, createSection(container));
  };

  const unloadSection = (id) => {
    const inst = instances.get(id);
    if (inst) { inst.destroy(); instances.delete(id); }
  };

  const bootAll = () =>
    document.querySelectorAll('[data-section-type="dvrw-shop-the-look"]').forEach(initSection);

  if (document.readyState !== 'loading') bootAll();
  else document.addEventListener('DOMContentLoaded', bootAll);

  document.addEventListener('shopify:section:load', (e) => {
    const node = document.getElementById(`shopify-section-${e.detail.sectionId}`);
    if (!node) return;
    const section = node.querySelector('[data-section-type="dvrw-shop-the-look"]');
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const node = document.getElementById(`shopify-section-${e.detail.sectionId}`);
    if (!node) return;
    const section = node.querySelector('[data-section-type="dvrw-shop-the-look"]');
    if (section) unloadSection(section.dataset.sectionId);
  });

  window.DvrwShopTheLook = { initSection, unloadSection, instances };
})();
